import { withinRange } from '../math/fixed.ts';
import { rngInt } from '../math/rng.ts';
import { cellCenter } from '../grid.ts';
import type { BattleState, GearRules, GearState, Hero, SendaNpc } from '../types.ts';
import { ev } from './combat.ts';
import { orderMove } from './heroes.ts';
import { gearTrain, trainRight } from './taller-layout.ts';
import { golemPost, golemsUp, golemTick, golemWake } from './golems.ts';
import { shootTick } from './workshop.ts';

/**
 * The wheel and the belt (docs/sendas/artifice.md, chapter 1, act 3, the test; §4, puzzle 1, «La rueda y la correa»).
 * The lift down to Tormo's cavern is stuck: its drum wants turning the other way from the water wheel, at the speed
 * its dial asks. In the hall's floor a bed of axles (battle/taller-layout.ts); gears of 12, 24 and 36 teeth lie about
 * the hall, counted. A tap on a gear sends the hero to take it, a tap on an axle to put the gear in its hands there (a
 * tap anywhere swaps what is in its hands with what is there: `gearOrder`), standing still `stand` by it. A gear in its
 * hands slows it, the bigger the more (`carryBp`: iron is heavy, and the golems are slow but don't stop).
 *
 * Two gears on axles next to each other mesh: they turn opposite ways, the speed changing by their teeth. With one gear
 * an axle the middle ones cancel out: the drum's speed is the wheel's times the wheel gear's teeth over the drum
 * gear's; the middles only turn the way round. An axle turned both ways at once (a ring of three) jams the train: it
 * stops, sparks, and a golem wakes. A golem wakes every `every` anyway, `cap` awake at most. The train right, the belt
 * takes it `settle` later, the lift's chain goes taut: and the Overseer by the lift wakes (two boilers: both open at
 * once put it out, battle/golems.ts). Won with the Overseer out (or broken) and the belt on. Dunna keeps by the door
 * with her crossbow.
 *
 * Cost: the train is worked out again only when a gear is put or taken (eight axles at most: a walk over them).
 */

const fresh = (r: GearRules): GearState => {
  const A = r.axles.length;
  return {
    on: r.pieces.map((_, k) => A + k),
    hand: -1,
    job: -1,
    held: 0,
    dir: r.axles.map(() => 0),
    speed: r.axles.map(() => 0),
    jam: -1,
    errors: 0,
    next: r.first,
    woken: 0,
    solved: -1,
    belt: -1,
    boss: 0,
    down: -1,
    swing: 0,
  };
};

/** A slot's cell: an axle (by index), or a lying spot (the axles' count + the piece's own). */
export const gearSlotCell = (r: GearRules, slot: number): number => (slot < r.axles.length ? (r.axles[slot] as number) : (r.lie[slot - r.axles.length] as number));

/** The teeth on each axle as the gears lie now (0: none). */
export const gearSet = (r: GearRules, w: GearState): number[] => {
  const set = r.axles.map(() => 0);
  w.on.forEach((slot, k) => {
    if (slot >= 0 && slot < r.axles.length) set[slot] = r.pieces[k] as number;
  });
  return set;
};

/** The hero's pace with a gear in its hands (the bigger, the slower: SENDA.gears.carryBp by its teeth). */
export const gearCarryBp = (s: BattleState): number => {
  const r = s.rules?.senda?.gears;
  const w = s.senda?.gears;
  if (!r || !w || w.hand < 0) return 10000;
  const teeth = r.pieces[w.hand] as number;
  return r.carryBp[teeth >= 36 ? 2 : teeth >= 24 ? 1 : 0] as number;
};

/** The piece at a slot (−1: none). */
export const gearAt = (w: GearState, slot: number): number => w.on.indexOf(slot);

/** The hero sent to a slot (a tap on its mark): it walks there and swaps what is in its hands with what is there. */
export const gearOrder = (s: BattleState, h: Hero, at: number): void => {
  const r = s.rules?.senda?.gears;
  const w = s.senda?.gears;
  if (!r || !w || w.belt >= 0 || at < 0 || at >= r.axles.length + r.pieces.length) return;
  w.job = at;
  w.held = 0;
  const c = cellCenter(s.map.radius, gearSlotCell(r, at));
  orderMove(s, h, c.x, c.y);
  h.target = 0;
  h.forced = false;
};

/** The train worked out again: its ways and speeds, a jam (an error, a golem), the drum turning right. */
const retrain = (s: BattleState, r: GearRules, w: GearState, cue: (c: string) => void): void => {
  const was = w.jam;
  const t = gearTrain(r.adj, gearSet(r, w), r.wheel);
  w.dir = t.dir;
  w.speed = t.speed;
  w.jam = t.jam;
  if (t.jam >= 0 && was < 0) {
    w.errors++;
    const c = cellCenter(s.map.radius, r.axles[t.jam] as number);
    ev(s, { t: 'senda', kind: 'jam', npc: '', x: c.x, y: c.y, n: t.jam });
    cue(`jam${w.errors}`);
    wakeOne(s, r, w, 1);
  }
  if (t.drumSpeed) {
    const c = cellCenter(s.map.radius, r.axles[1] as number);
    ev(s, { t: 'senda', kind: 'turn', npc: '', x: c.x, y: c.y, n: t.drumDir * t.drumSpeed });
  }
  if (trainRight(t, r.want)) {
    if (w.solved < 0) {
      w.solved = s.tick;
      const c = cellCenter(s.map.radius, r.axles[1] as number);
      ev(s, { t: 'senda', kind: 'right', npc: '', x: c.x, y: c.y });
      cue('right');
    }
  } else w.solved = -1;
};

/** The hero's hands at the slot: standing still by it `stand`, what is in them and what is there change places. */
const heroWork = (s: BattleState, r: GearRules, w: GearState, cue: (c: string) => void): void => {
  const h = s.heroes.find((x) => x.downTicks === 0);
  if (!h || w.job < 0) return;
  const c = cellCenter(s.map.radius, gearSlotCell(r, w.job));
  if (h.moving || !withinRange(h.x, h.y, c.x, c.y, r.reach)) {
    w.held = 0;
    if (!h.moving) w.job = -1;
    return;
  }
  if (++w.held < r.stand) return;
  const there = gearAt(w, w.job);
  const hand = w.hand;
  if (there < 0 && hand < 0) {
    w.job = -1;
    return;
  }
  if (there >= 0) {
    w.on[there] = -1;
    ev(s, { t: 'senda', kind: 'take', npc: '', x: c.x, y: c.y, n: w.job });
  }
  if (hand >= 0) {
    w.on[hand] = w.job;
    ev(s, { t: 'senda', kind: 'put', npc: '', x: c.x, y: c.y, n: w.job });
  }
  w.hand = there;
  if (w.job < r.axles.length) retrain(s, r, w, cue);
  w.job = -1;
  w.held = 0;
};

/** Wakes one more golem at the hall's wall (the next asleep; a broken one's post gets a new one): `extra` over the cap. */
const wakeOne = (s: BattleState, r: GearRules, w: GearState, extra = 0): void => {
  const gr = s.rules?.senda?.golem;
  const list = s.senda?.golem?.list;
  if (!gr || !list || golemsUp(s) >= r.cap + extra) return;
  const asleep = list.filter((g) => g.mode === 0 && !g.boss);
  let g = asleep.length ? asleep[rngInt(s.rng, asleep.length)] : undefined;
  if (!g) {
    const p = r.posts[w.woken % r.posts.length] as GearRules['posts'][number];
    g = golemPost(s, gr, p, -1);
  }
  w.woken++;
  golemWake(s, g);
};

export const gearsTick = (s: BattleState, cue: (c: string) => void): void => {
  const r = s.rules?.senda?.gears;
  const gr = s.rules?.senda?.golem;
  const st = s.senda;
  if (!r || !gr || !st) return;
  if (!st.gears) {
    st.gears = fresh(r);
    st.golem = { list: [], pend: [] };
    for (const p of r.posts) golemPost(s, gr, p, -1);
    st.gears.boss = golemPost(s, gr, r.boss, -1, true).id;
  }
  const w = st.gears;
  if (!s.started) return;
  golemTick(s, cue);
  heroWork(s, r, w, cue);
  // A golem every so often, the Overseer's fight too.
  if (s.waveT >= w.next && w.down < 0) {
    w.next = s.waveT + r.every;
    wakeOne(s, r, w);
    cue(`golem${w.woken}`);
  }
  // The belt takes the train; the Overseer wakes.
  if (w.solved >= 0 && w.belt < 0 && s.tick >= w.solved + r.settle) {
    w.belt = s.tick;
    const c = cellCenter(s.map.radius, r.axles[1] as number);
    ev(s, { t: 'senda', kind: 'belt', npc: '', x: c.x, y: c.y });
    cue('belt');
    const boss = st.golem?.list.find((g) => g.id === w.boss);
    if (boss) golemWake(s, boss, cue);
    // What it had in its hands it drops (the train is done).
    w.hand = -1;
    w.job = -1;
  }
  if (w.belt >= 0 && w.down < 0) {
    const boss = st.golem?.list.find((g) => g.id === w.boss);
    if (!boss || boss.mode === 3 || (boss.mode === 2 && boss.t === 0x7fffffff)) w.down = s.tick;
  }
  const d = st.npcs.find((n): n is SendaNpc => n.id === 'dunna' && n.hp > 0);
  if (d) w.swing = shootTick(s, d, r.dunna, w.swing);
};

/** The belt on, the Overseer out: two steps. */
export const gearsGoal = (s: BattleState): { done: number; need: number } => {
  const w = s.senda?.gears;
  if (!w) return { done: 0, need: 2 };
  return { done: (w.belt >= 0 ? 1 : 0) + (w.down >= 0 ? 1 : 0), need: 2 };
};

/** Won the Overseer out with the belt on (a moment after, the lift going down). */
export const gearsResult = (s: BattleState): 'won' | null => {
  const w = s.senda?.gears;
  return w && w.down >= 0 && s.tick >= w.down + 18 ? 'won' : null;
};
